import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {getDateFormat, getTimeFormat, DATE_TIME_FORMAT_NAME_MODEL} from '../../../../../../../Apis/functions';
import { GetPayrollResultById } from '../../../../../../../reduxToolkit/PayrollPolicy/PayrollPolicyApi';
import {
    getCalculationTypeById, getConflictResolutionById, getStackMethodById,
    POLICY_CONDITION_MODEL
} from "../../../OvertimePolicies/Global/CreateUpdatePolicy/Model/Model";
import {IconGraphDown, IconGraphGrowth, IconScheduleTable} from "../../../../../../../components/buttons/IconButtons";
import {colors} from "../../../../../../../enums/colors";
import PayrollLinesColumn, {PayrollLineRow, getWidestAmount} from "./PayrollLinesColumn";
import PayrollSummaryCards from "./PayrollSummaryCards";
import NotInformation from "../../../../../../../components/NotInformation";
import CustomChip from "../../../../../../../components/CustomChip";
import CustomUserChip from "../../../../../../../components/CustomUserChip";
import {POSITION_MODEL} from "../../../../../../../Helpers/PositionModel";
import { getOvertimeBreakdown } from "../Model/Model";
/**
 * @Author: Karen Yunuen Pozos Arias
 * @JobTitle: Full Stack Developer - IBL
 * @Email: kpozos@ibl.mx
 */
const ActivePeriodTab = ({ id }) => {
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const [activePeriod, setActivePeriod] = useState(null);

    useEffect(() => {
        if (!id) return;
        dispatch(GetPayrollResultById(id)).then(({ payload: { data: { data, success } } }) => {
            if (success) setActivePeriod(data);
        });
    }, [id]);

    const widestOvertime = getWidestAmount(
        [...(activePeriod?.overtimeLines ?? []), { result: activePeriod?.baseSalary }],
        'result',
        activePeriod?.totalOvertime
    );
    const widestDeduction = getWidestAmount(activePeriod?.deductionsLines, 'amount', activePeriod?.totalDeductions);

    // Active period legend: start date+time, end date and the closing time.
    const periodStart = `${getDateFormat(activePeriod?.periodStartDate, false, DATE_TIME_FORMAT_NAME_MODEL.shortMonth)} ${getTimeFormat(activePeriod?.periodStartDate, DATE_TIME_FORMAT_NAME_MODEL.HHmmss)}`;
    const periodEnd = getDateFormat(activePeriod?.periodEndDate, false, DATE_TIME_FORMAT_NAME_MODEL.shortMonth);
    const periodEndTime = getTimeFormat(activePeriod?.periodEndDate, DATE_TIME_FORMAT_NAME_MODEL.HHmmss);

    return (
        <div className='p-3'>
            { activePeriod === null ?
                <NotInformation card={true} text={t('there_not_period_active')} />
                :
                <>
                    <CustomUserChip
                        id={'chip_apt_active_period'}
                        name={t('active_period_range', { start: periodStart, end: periodEnd, endTime: periodEndTime })}
                        showAvatar={true}
                        Icon={IconScheduleTable}
                        iconPosition={POSITION_MODEL.LEFT}
                        className={'chip-secondary-150-100 mb-3 mt-0 w-100'}
                        classNameIcon={'icon-color-secondary-700'}
                        variant='large'
                    />
                    <PayrollSummaryCards data={activePeriod} />
                    <div className='row'>
                        <PayrollLinesColumn
                            title={t('perceptions')}
                            tooltip={<>
                                <p className={"ibl-font-w-600 mb-1"}>{t('conflict_resolution')}</p>
                                <CustomChip
                                    background={colors.shades100}
                                    borderColor={colors.shades150}
                                    fontColor={colors.shades700}
                                    legend={t(getConflictResolutionById(activePeriod?.groupConflictResolutionSnapshot)?.name)}
                                    variant={'large'}
                                />
                                {activePeriod?.groupStackMethodSnapshot &&
                                <>
                                    <p className={"ibl-font-w-600 mt-2 mb-1"}>{t('stack_method')}</p>
                                    <CustomChip
                                        background={colors.shades100}
                                        borderColor={colors.shades150}
                                        fontColor={colors.shades700}
                                        legend={t(getStackMethodById(activePeriod?.groupStackMethodSnapshot)?.name)}
                                        variant={'large'}
                                    />
                                </>
                                }
                            </>}
                            lines={activePeriod?.overtimeLines}
                            getName={(item) => item.policyNameSnapshot}
                            getLegend={(item) => POLICY_CONDITION_MODEL[item?.policyConditionSnapshot]?.legend(
                                item?.totalOvertimeMinutes,
                                item?.policyCalculationTypeSnapshot,
                                item?.policyCalculationValueSnapshot
                            )}
                            getBreakdown={(item) => getOvertimeBreakdown(item, activePeriod)}
                            getAmount={(item) => item.result}
                            widest={widestOvertime}
                            total={activePeriod?.totalOvertime + activePeriod?.baseSalary}
                            titleIcon={<IconGraphGrowth fill={colors.secondary700}/>}
                            chipColors={{ background: colors.secondary100, fontColor: colors.secondary700, borderColor: colors.secondary150 }}
                            leadingRow={
                                <PayrollLineRow
                                    name={`${t('base_salary')}`}
                                    amount={activePeriod?.baseSalary}
                                    widest={widestOvertime}
                                    legend={t('payment_an_employee_receives_for_their_regular_working_hours')}
                                    leadingRow={true}
                                    breakdown={activePeriod}
                                />
                            }
                        />
                        <PayrollLinesColumn
                            title={t('deductions')}
                            lines={activePeriod?.deductionsLines}
                            getName={(item) => item.deductionNameSnapshot}
                            getLegend={(item) => `- ${getCalculationTypeById(item.deductionCalculationTypeSnapshot)?.prefixText}${item.deductionValueSnapshot}`}
                            getAmount={(item) => item.amount}
                            widest={widestDeduction}
                            total={activePeriod?.totalDeductions}
                            titleIcon={<IconGraphDown fill={colors.errorCode700}/>}
                            chipColors={{ background: colors.errorCode100, fontColor: colors.errorCode700, borderColor: colors.errorCode150 }}
                        />
                    </div>
                </>
            }
        </div>
    );
};

export default ActivePeriodTab;
